import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import FormModal from '../components/FormModal';
import { ActionsSkeletonCell } from '../components/ActionsSkeletonCell';
import { FormErrorBanner } from '../components/FormErrorBanner';
import { NoProjectSelectedState } from '../components/NoProjectSelectedState';
import { Skeleton } from '../components/Skeleton';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { yellowFlagAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';

// Mirrors the shared Reason type from the Yellow Flag build contract.
interface YellowFlagReason {
  id: number;
  reasonCode: string;
  label: string;
  active: boolean;
  sortOrder: number;
}

export default function YellowFlagReasonsPage() {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);

  const [reasons, setReasons] = useState<YellowFlagReason[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingReason, setEditingReason] = useState<YellowFlagReason | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<YellowFlagReason | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [togglingId, setTogglingId] = useState<number | null>(null);
  // Non-fatal error channel for row mutations (toggle / delete). Distinct from
  // `error`, which the table treats as a FATAL full-table state (fetch failure).
  const [actionError, setActionError] = useState<string | null>(null);

  const [formState, setFormState] = useState({
    label: '',
    sortOrder: '',
    isActive: true,
  });

  const fetchReasons = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await yellowFlagAPI.getReasons(selectedProjectId);
      if (response.data?.success) {
        setReasons(response.data.data || []);
      } else {
        setReasons(response.data?.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch yellow flag reasons:', err);
      setError(err.response?.data?.message || 'Failed to fetch reasons');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    fetchReasons();
  }, [fetchReasons]);

  const resetForm = () => {
    setFormState({ label: '', sortOrder: '', isActive: true });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    if (!formState.label.trim()) {
      setFormError('Reason label is required.');
      return;
    }

    setIsSubmitting(true);

    try {
      const sortOrder = formState.sortOrder ? Number(formState.sortOrder) : 0;

      if (modalMode === 'create') {
        await yellowFlagAPI.createReason(selectedProjectId!, {
          label: formState.label.trim(),
          sortOrder,
        });
      } else if (editingReason) {
        await yellowFlagAPI.updateReason(editingReason.id, {
          label: formState.label.trim(),
          active: formState.isActive,
          sortOrder,
        });
      }

      setIsModalOpen(false);
      resetForm();
      setFormError(null);
      fetchReasons();
    } catch (err: any) {
      console.error('Failed to save yellow flag reason:', err);
      setFormError(err.response?.data?.message || 'Failed to save reason');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (reason: YellowFlagReason) => {
    try {
      setIsSubmitting(true);
      setActionError(null);
      await yellowFlagAPI.deleteReason(reason.id);
      setDeleteConfirm(null);
      fetchReasons();
    } catch (err: any) {
      // Hard delete isn't idempotent — a retry / concurrent delete 404s on an
      // already-removed reason. The desired end-state (gone) is met, so treat it
      // as success rather than surfacing an error.
      if (err.response?.status === 404) {
        setDeleteConfirm(null);
        fetchReasons();
        return;
      }
      console.error('Failed to delete yellow flag reason:', err);
      setActionError(err.response?.data?.message || 'Failed to delete reason');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Quick active/inactive toggle straight from the row — a reversible alternative
  // to the permanent Delete. Flips the row in place on success (no full refetch).
  const handleToggleActive = async (reason: YellowFlagReason) => {
    const next = !reason.active;
    try {
      setTogglingId(reason.id);
      setActionError(null);
      await yellowFlagAPI.updateReason(reason.id, { active: next });
      // Assign the value we actually PUT (not !r.active off live state), so
      // overlapping updates can't double-flip the row.
      setReasons((prev) => prev.map((r) => (r.id === reason.id ? { ...r, active: next } : r)));
    } catch (err: any) {
      console.error('Failed to toggle yellow flag reason:', err);
      setActionError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setTogglingId(null);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setFormError(null);
    setModalMode('create');
    setEditingReason(null);
    setIsModalOpen(true);
  };

  const openEditModal = (reason: YellowFlagReason) => {
    setModalMode('edit');
    setEditingReason(reason);
    setFormError(null);
    setFormState({
      label: reason.label,
      sortOrder: String(reason.sortOrder || 0),
      isActive: reason.active,
    });
    setIsModalOpen(true);
  };

  const filteredReasons = useMemo(() => {
    if (!searchQuery) return reasons;
    const q = searchQuery.toLowerCase();
    return reasons.filter(
      (r) => r.label.toLowerCase().includes(q) || r.reasonCode.toLowerCase().includes(q)
    );
  }, [reasons, searchQuery]);

  if (!selectedProjectId) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Yellow Flag Reasons</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage the reasons a manager can pick when raising a Yellow Flag during a store visit.
          </p>
        </div>
        <NoProjectSelectedState message="Select a project from the dropdown in the menu bar." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Yellow Flag Reasons</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage the reasons a manager can pick when raising a Yellow Flag during a store visit.
          Until a project configures its own reasons, the mobile app falls back to a built-in default set.
        </p>
      </div>

      <div className="flex items-center justify-end gap-4">
        <button
          className="flex-shrink-0 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
          onClick={openCreateModal}
        >
          Add New Reason
        </button>
      </div>

      {actionError && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="shrink-0 rounded p-0.5 text-red-500 hover:bg-red-100 hover:text-red-700"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Table (search + table in one container) */}
      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="border-b border-gray-100 p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by label or code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <th className="whitespace-nowrap px-6 py-4">Label</th>
                <th className="whitespace-nowrap px-6 py-4">Code</th>
                <th className="whitespace-nowrap px-6 py-4">Sort Order</th>
                <th className="whitespace-nowrap px-6 py-4">Status</th>
                <th className="sticky right-0 z-10 whitespace-nowrap bg-gray-100 px-6 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white text-left">
              {loading ? (
                <>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index}>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-40" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-12" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-5 w-16 rounded-full" /></td>
                      <ActionsSkeletonCell />
                    </tr>
                  ))}
                </>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <p className="text-sm text-red-500">{error}</p>
                    <button
                      onClick={fetchReasons}
                      className="mt-2 text-sm text-blue-600 hover:underline"
                    >
                      Try again
                    </button>
                  </td>
                </tr>
              ) : filteredReasons.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <p className="text-sm text-gray-500">
                      {reasons.length === 0
                        ? 'No reasons configured. The mobile app uses the built-in defaults until you add one.'
                        : 'No reasons match your search.'}
                    </p>
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="mt-2 text-sm text-blue-600 hover:underline"
                      >
                        Clear search
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                filteredReasons.map((reason) => (
                  <tr key={reason.id} className="group hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{reason.label}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-left font-mono text-xs text-gray-500">{reason.reasonCode}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{reason.sortOrder}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-left">
                      <div className="flex items-center gap-2.5">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={reason.active}
                          aria-label={`${reason.active ? 'Deactivate' : 'Activate'} ${reason.label}`}
                          disabled={togglingId === reason.id}
                          onClick={() => handleToggleActive(reason)}
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                            reason.active ? 'bg-primary-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              reason.active ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <span className={`text-xs font-medium ${reason.active ? 'text-green-700' : 'text-gray-500'}`}>
                          {reason.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-6 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-50">
                      <TableActionGroup>
                        <TableActionButton action="edit" label={`Edit ${reason.label}`} type="button" onClick={() => openEditModal(reason)} />
                        <TableActionButton action="delete" label={`Delete ${reason.label}`} type="button" onClick={() => setDeleteConfirm(reason)} />
                      </TableActionGroup>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <FormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={modalMode === 'create' ? 'Add New Reason' : 'Edit Reason'}
        size="md"
        formId="yellow-flag-reason-form"
        isSubmitting={isSubmitting}
        submitLabel={modalMode === 'create' ? 'Add Reason' : 'Save Changes'}
      >
        <form id="yellow-flag-reason-form" className="space-y-6" onSubmit={handleSubmit}>
          {formError && <FormErrorBanner message={formError} />}

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="yf-reason-label">
              Label
            </label>
            <input
              id="yf-reason-label"
              type="text"
              value={formState.label}
              onChange={(e) => setFormState((prev) => ({ ...prev, label: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="e.g. Grooming / Uniform, Punctuality / Late"
              required
            />
            <p className="text-xs text-gray-500">A short code is generated automatically from the label.</p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="yf-sort-order">
              Sort Order
            </label>
            <input
              id="yf-sort-order"
              type="number"
              min="0"
              value={formState.sortOrder}
              onChange={(e) => setFormState((prev) => ({ ...prev, sortOrder: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="0"
            />
            <p className="text-xs text-gray-500">Lower numbers appear first in the mobile picker.</p>
          </div>

          {modalMode === 'edit' && (
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700" htmlFor="yf-is-active">
                Active
              </label>
              <button
                id="yf-is-active"
                type="button"
                role="switch"
                aria-checked={formState.isActive}
                onClick={() => setFormState((prev) => ({ ...prev, isActive: !prev.isActive }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formState.isActive ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formState.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-500">
                {formState.isActive ? 'Shown in mobile picker' : 'Hidden from mobile picker'}
              </span>
            </div>
          )}
        </form>
      </FormModal>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        isSubmitting={isSubmitting}
        title="Delete Reason"
        entityLabel="reason"
        entityName={deleteConfirm?.label}
      />
    </div>
  );
}
